import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { InitData, CustomersProduct, SearchResultMeta } from '@/models/customersTypes';
import { customersProductsApi } from '@/api/api';
import { showToast } from '@/components/UI/Toast';
import defaultImg from '@/public/img/imgicon.png';
import { Link, useSearchParams } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/navigation';
import LoadingIcon from '@/components/UI/LoadingIcon';
import SeoHelmet from '@/components/UI/SeoHelmet';
import seoImg from "@/public/img/seo/produkte.png";
import { FaArrowDown, FaArrowRight, FaMapMarkerAlt } from 'react-icons/fa';
import PageHero from '@/components/UI/PageHero';
import ActionLink from '@/components/UI/ActionLink';
import alltag from '@/public/img/einkauf-alltag.png';
import foodMarket from '@/public/svg/food-market.svg';
import PressReleaseDialog from '@/components/UI/PressReleaseDialog';
import HomeIntro from '@/components/UI/HomeIntro';
import Filters, { CategorySource } from '@/components/UI/ProductFilters';
import close from '@/public/svg/close.svg';
import aboutUs from '@/public/svg/about-us.svg';
import { isDiscountActive } from '@/helpers/discounts';
import { track } from '@/helpers/analytics';

type TrackIntent = { search: boolean; filter?: CategorySource };

const CustomersProductList: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const [initData, setInitData] = useState<InitData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [searchResult, setSearchResult] = useState<CustomersProduct[] | null>(null);
    const [searchMeta, setSearchMeta] = useState<SearchResultMeta | null>(null);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const initialQ = searchParams.get("q") ?? '';
    const initialCat = searchParams.get("cat") ?? '';

    const q = searchParams.get("q") ?? "";
    const cat = searchParams.get("cat") ?? "";

    const [searchValue, setSearchValue] = useState(initialQ);
    const [selectedCategory, setSelectedCategory] = useState(initialCat);
    const [searchValueText, setSearchValueText] = useState(initialQ);

    const [pendingSearchValue, setPendingSearchValue] = useState(q);
    const [pendingCategory, setPendingCategory] = useState(cat);
    const pendingCategorySourceRef = useRef<CategorySource | undefined>(undefined);
    const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const trackIntentRef = useRef<TrackIntent | null>(null);

    const [showPressRelease, setShowPressRelease] = useState(false);
    const closePressRelease = useCallback(() => setShowPressRelease(false), []);

    const hasFilter = searchValue.length > 0 || selectedCategory.length > 0;

    const defaultPageSize = 24;
    const itemsPerPage = searchMeta?.page_size ?? defaultPageSize;
    const totalResults = searchMeta?.total ?? (searchResult?.length ?? 0);
    const visibleCount = searchResult?.length ?? 0;
    const isLastPage = visibleCount >= totalResults;

    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (q) {
            next.set("q", q);
        } else {
            next.delete("q");
        }
        if (cat) {
            next.set("cat", cat);
        } else {
            next.delete("cat");
        }
        setSearchParams(next, { replace: true });
    }, [q, cat]);

    useEffect(() => {
        const fetchInit = async () => {
            setIsLoading(true);
            try {
                const data = await customersProductsApi.getInitData();
                setInitData(data);
            } catch (error) {
                showToast('Produkte konnten nicht geladen werden', 'error');
                console.error(error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchInit();
    }, []);


    useEffect(() => {
        setSearchValue(q);
        setSelectedCategory(cat);
        setSearchValueText(q);
    }, [q, cat]);

    const loadProducts = useCallback(async (page: number, options?: { append?: boolean }) => {
        if (!initData) return;
        const isAppend = options?.append ?? false;
        const trackIntent = isAppend ? null : trackIntentRef.current;
        if (!isAppend) trackIntentRef.current = null;

        if (isAppend) {
            setIsLoadingMore(true);
        } else {
            setIsLoading(true);
        }

        try {
            const catId = cat && initData?.categories.find(c => c.name === cat)?.id;
            const res = await customersProductsApi.getProducts({
                q: q || undefined,
                cat: catId || undefined,
                p: page,
                page_size: defaultPageSize,
            });
            setSearchMeta(res.meta);
            if (trackIntent?.search) track('product-search', { results: res.meta.total, has_results: res.meta.total > 0 });
            if (trackIntent?.filter) track('product-filter', { category: cat, source: trackIntent.filter });
            setSearchResult(prev => (isAppend && prev ? [...prev, ...res.data] : res.data));
            setCurrentPage(page);
            setSearchValue(q);
            setSelectedCategory(cat);
        } catch (error) {
            showToast('Produkte konnten nicht geladen werden', "error");
            console.error('Error fetching init:', error);
        } finally {
            if (isAppend) {
                setIsLoadingMore(false);
            } else {
                setIsLoading(false);
            }
        }
    }, [cat, defaultPageSize, initData, q]);

    useEffect(() => {
        if (!initData) return;
        if (!q && !cat) return;
        loadProducts(1);
    }, [loadProducts, initData, q, cat]);

    const searchProduct = (value = searchValue, category = selectedCategory, source?: CategorySource) => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = null;
        }

        setCurrentPage(1);

        const nextValue = typeof value === "string" ? value : searchValue;
        const nextCategory = typeof category === "string" ? category : selectedCategory;
        trackIntentRef.current = {
            search: !!nextValue && nextValue !== q,
            filter: nextCategory && nextCategory !== cat ? source : undefined,
        };

        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (nextValue) {
                next.set("q", nextValue);
            } else {
                next.delete("q");
            }
            if (nextCategory) {
                next.set("cat", nextCategory);
            } else {
                next.delete("cat");
            }
            return next;
        });
    };

    const fetchByCategory = (category: string, source: CategorySource) => {
        searchProduct(searchValue, category, source);
        setCurrentPage(1);
    };

    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            if (searchValue) {
                searchProduct();
            }
        }, 1500);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchValue]);

    const resetFilters = () => {
        setSearchParams({});
        setSearchValue("");
        setSelectedCategory("");
        setSearchResult(null);
        setSearchMeta(null);
        setCurrentPage(1);
        setIsFilterOpen(false);
    };

    const resetPendingFilters = () => {
        setPendingSearchValue("");
        setPendingCategory("");
    };

    const resetMobileFilters = () => {
        resetFilters();
        resetPendingFilters();
    };

    const openFilterSheet = () => {
        setPendingSearchValue(searchValue);
        setPendingCategory(selectedCategory);
        setIsFilterOpen(true);
    };

    const closeFilterSheet = () => {
        setPendingSearchValue(searchValue);
        setPendingCategory(selectedCategory);
        setIsFilterOpen(false);
    };

    const applyMobileFilters = () => {
        setIsFilterOpen(false);
        setSelectedCategory(pendingCategory);
        setSearchValue(pendingSearchValue);
        searchProduct(pendingSearchValue, pendingCategory, pendingCategorySourceRef.current);
    };

    const visibleResults = useMemo(() => {
        return searchResult ?? [];
    }, [searchResult]);

    const offers = useMemo(() => initData?.special_offers ?? [], [initData]);

    const STATE_KEY = 'productListState';

    const saveScrollPosition = () => {
        sessionStorage.setItem(STATE_KEY, JSON.stringify({
            y: window.scrollY,
            search: window.location.search,
        }));
    };

    // Restore search params on mount in case React Router lost them during back navigation
    useEffect(() => {
        const raw = sessionStorage.getItem(STATE_KEY);
        if (!raw) return;
        const { search } = JSON.parse(raw) as { y: number; search: string };
        if (search && !window.location.search) {
            setSearchParams(new URLSearchParams(search.replace(/^\?/, '')), { replace: true });
        }
    }, []); // mount-only: intentionally no deps

    useEffect(() => {
        if (isLoading || searchResult === null) return;
        const raw = sessionStorage.getItem(STATE_KEY);
        if (!raw) return;
        sessionStorage.removeItem(STATE_KEY);
        const { y } = JSON.parse(raw) as { y: number; search: string };
        requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo({ top: y, behavior: 'instant' })));
    }, [isLoading, searchResult]);

    if (isLoading) return (<LoadingIcon text="Lade Produkte ..." />);

    return (
        <>
            <SeoHelmet
                title="Yobsti – Aktionen & Angebote für Produkte des täglichen Bedarfs"
                description="Entdecken Sie aktuelle Angebote bei Yobsti – Ihrem digitalen Dorfladen. Frische Lebensmittel, regionale Produkte und starke Aktionen direkt im Ort."
                image={seoImg}
                url="https://www.yobsti.de/produkte"
            />

            <PageHero
                eyebrow="frisch. lokal. smart."
                title={<>Für deinen Alltag.<br />Gleich um die Ecke.</>}
                text="Entdecke unser Sortiment und aktuelle Angebote."
                illustration={alltag}
            >
                <div className="mt-8 flex flex-wrap gap-3">
                    <ActionLink to="/standorte" variant="solid" event="cta-click" eventData={{ target: 'find-store', location: 'home-hero' }}>
                        <FaMapMarkerAlt className="h-4 w-4" aria-hidden="true" />
                        Deinen Yobsti finden
                    </ActionLink>
                    <ActionLink to="#angebote" event="cta-click" eventData={{ target: 'offers', location: 'home-hero' }}>
                        Angebote entdecken
                        <FaArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
                    </ActionLink>
                </div>
            </PageHero>

            <div className="w-full bg-secondaryLight text-textColor" data-testid="teo-transition">
                <div className="mx-auto flex max-w-[1171px] flex-wrap items-center justify-center gap-x-5 gap-y-2 px-4 py-4">
                    <span className="flex items-center gap-3 text-lg font-bold">
                        <img src={foodMarket} alt="" className="h-8 w-8" />
                        Aus teo wird Yobsti.
                    </span>
                    <span className="hidden h-6 w-px bg-textColor/40 sm:block" aria-hidden="true" />
                    <button
                        type="button"
                        onClick={() => {
                            track('press-release-open');
                            setShowPressRelease(true);
                        }}
                        className="inline-flex items-center gap-2 font-semibold underline underline-offset-4 hover:text-secondaryDark"
                    >
                        Mehr zur Übernahme
                        <FaArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                </div>
            </div>
            <PressReleaseDialog show={showPressRelease} onClose={closePressRelease} />

            <div className="mx-auto w-full max-w-[1171px] px-4 2xl:px-0">
                <section id="angebote" className="scroll-mt-4 flex flex-col gap-6 md:grid md:grid-cols-[320px_minmax(0,1fr)] md:gap-x-10 xl:grid-cols-[320px_minmax(0,1fr)_272px] xl:gap-x-6">
                    <div className="pt-6 md:col-span-2 xl:col-span-1 xl:col-start-3 xl:row-start-1 xl:pt-8">
                        <HomeIntro />
                    </div>

                    <aside className="hidden md:flex md:row-start-2 xl:row-start-1 flex-col gap-6 pr-6 border-r border-[#DFDED1]">
                        {initData &&
                            <Filters
                                initData={initData}
                                selectedCategory={selectedCategory}
                                searchValue={searchValue}
                                autoFocus={!isFilterOpen}
                                focusKey={`${searchValue}-${selectedCategory}`}
                                onCategoryChange={(v, source) => {
                                    setSelectedCategory(v);
                                    if (v == "angebote") {
                                        track('product-filter', { category: v, source });
                                        resetFilters();
                                    } else {
                                        fetchByCategory(v, source);
                                    }
                                }}
                                onSearchChange={(v) => setSearchValue(v)}
                                onSearchSubmit={searchProduct}
                                onResetFilters={resetFilters}
                            />
                        }
                    </aside>

                    <main className="w-full min-w-0 md:pt-8 md:row-start-2 md:col-start-2 xl:row-start-1">
                        {/* Mobile Filter Button */}
                        <button
                            className="md:hidden w-full bg-primary text-textColor px-4 py-3 rounded-full font-semibold mb-4"
                            onClick={openFilterSheet}
                        >
                            Produkte filtern
                        </button>

                        <Link
                            to="/links"
                            data-testid="follow-us-button"
                            onClick={() => track('cta-click', { target: 'links', location: 'home' })}
                            className="fixed right-0 top-[80%] z-50 -translate-y-1/2 bg-secondary text-textColor font-semibold px-4 py-3 rounded-l-full shadow hover:bg-primary cursor-pointer transition"
                        >
                            Folge uns
                        </Link>

                        <section className="flex flex-col mx-auto w-full max-w-[1171px] px-4 2xl:px-0">
                            {!hasFilter && (
                                <div data-testid="special-offers-container">
                                    <h1 data-testid="special-offers-header" className="mb-8 font-bold text-[24px]">Angebote ({offers.length})</h1>

                                    {offers.length > 0 ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-4 gap-3 mb-10">
                                            {offers.map((product) => {
                                                const image = product.image_url || defaultImg;
                                                const hasActiveDiscount = isDiscountActive(product);
                                                return (
                                                    <Link
                                                        to={`/produkte/${product.id}`}
                                                        key={product.id}
                                                        className="bg-white rounded-xl shadow hover:shadow-lg transition p-2 flex flex-col y-image-link"
                                                        onClick={saveScrollPosition}
                                                    >
                                                        <div className="flex-grow flex items-center justify-center">
                                                            <img
                                                                src={image}
                                                                alt={`Bild von ${product.name}`}
                                                                loading="lazy"
                                                                className="rounded-md object-contain y-image"
                                                            />
                                                        </div>
                                                        <div>
                                                            <h2 className="text-md font-medium line-clamp-2 h-[3.25rem]">{product.name}</h2>
                                                            {hasActiveDiscount ? (
                                                                <div className="flex items-baseline gap-2">
                                                                    <p className="text-md text-gray-500 line-through">
                                                                        {product.list_price.toFixed(2)} €
                                                                    </p>
                                                                    <p className="text-md font-bold text-secondary">
                                                                        {product.reduced_price.toFixed(2)} €
                                                                    </p>
                                                                </div>
                                                            ) : (
                                                                <p className="text-primary font-bold text-md">
                                                                    {product.list_price.toFixed(2)} €
                                                                </p>
                                                            )}
                                                        </div>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 text-gray-500 my-8">
                                            <span>Demnächst sind wieder tolle Angebote da</span>
                                            <img src={aboutUs} alt="" className="w-5 h-5" />
                                        </div>
                                    )}
                                </div>
                            )}

                            {searchResult != null && (searchValue?.length > 0 || selectedCategory?.length > 0) ?
                                (
                                    <>
                                        <button
                                            type="button"
                                            onClick={resetFilters}
                                            className="mb-4 flex items-center gap-2 text-primary font-semibold hover:text-primaryDark transition"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                                            </svg>
                                            Zurück zu Angeboten
                                        </button>
                                        <h1 className="mb-8 font-bold text-[24px]">{selectedCategory?.length > 0 ? selectedCategory : `Suche für ${searchValueText}:`} ({totalResults})</h1>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-4 gap-3 mb-10">
                                            {visibleResults.length > 0 ? (
                                                visibleResults.map((product) => {
                                                    const image = product.image_url || defaultImg;
                                                    const hasActiveDiscount = isDiscountActive(product);
                                                    return (
                                                        <Link
                                                            to={`/produkte/${product.id}`}
                                                            key={product.id}
                                                            className="bg-white rounded-xl shadow hover:shadow-lg transition p-2 flex flex-col y-image-link"
                                                        >
                                                            <div className="flex-grow flex items-center justify-center">
                                                                <img
                                                                    src={image}
                                                                    alt={`Bild von ${product.name}`}
                                                                    loading="lazy"
                                                                    className="rounded-md object-contain y-image"
                                                                    onError={(e) => { e.currentTarget.src = defaultImg; e.currentTarget.onerror = null; }}
                                                                />
                                                            </div>
                                                            <div>
                                                                <h2 className="text-md font-medium line-clamp-2 h-[3.25rem]">{product.name}</h2>
                                                                {hasActiveDiscount ? (
                                                                    <div className="flex items-baseline gap-2">
                                                                        <p className="text-md text-gray-500 line-through">
                                                                            {product.list_price.toFixed(2)} €
                                                                        </p>
                                                                        <p className="text-md font-bold text-secondary">
                                                                            {product.reduced_price.toFixed(2)} €
                                                                        </p>
                                                                    </div>
                                                                ) : (
                                                                    <p className="text-primary font-bold text-md">
                                                                        {product.list_price.toFixed(2)} €
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </Link>
                                                    );
                                                })
                                            ) : (
                                                <p className="text-center col-span-full text-gray-500">Keine Produkte gefunden.</p>
                                            )}
                                        </div>

                                        {totalResults > itemsPerPage && (
                                            <div className="mb-20 flex flex-col items-center gap-4">
                                                <p className="text-sm font-semibold text-textColor">
                                                    {visibleCount} von {totalResults} Produkten geladen
                                                </p>
                                                <div className="h-2.5 w-full max-w-xl overflow-hidden rounded-full bg-gray-200">
                                                    <div
                                                        className="h-full rounded-full bg-primary transition-all"
                                                        style={{ width: `${(visibleCount / totalResults) * 100}%` }}
                                                    />
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => loadProducts(currentPage + 1, { append: true })}
                                                    disabled={isLastPage || isLoadingMore}
                                                    className={`px-4 py-2 rounded-full transition whitespace-nowrap ${isLoadingMore
                                                        ? 'bg-transparent'
                                                        : 'bg-primary text-gray-700 hover:bg-primaryDark'
                                                        } ${isLastPage || isLoadingMore ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    {isLoadingMore ? <LoadingIcon text="" variant="inline" /> : "Weitere Produkte laden"}
                                                </button>
                                            </div>
                                        )}
                                    </>
                                ) : (<> </>)
                            }
                        </section>

                        {isFilterOpen && (
                            <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-end items-end md:hidden">
                                <div className="w-full h-[85vh] bg-white rounded-t-2xl p-6 overflow-y-auto animate-slideUp">
                                    <div className="flex justify-between items-center mb-4">
                                        <h2 className="text-xl font-bold">Produkte filtern</h2>

                                        <button
                                            onClick={closeFilterSheet}
                                            className="text-gray-500 hover:text-black text-2xl"
                                            aria-label="Schließen"
                                        >
                                            <img
                                                src={close}
                                                alt="Schließen"
                                                className="w-5 h-5"
                                            />
                                        </button>
                                    </div>

                                    <div className="mb-4">
                                        <button
                                            onClick={applyMobileFilters}
                                            className="w-full bg-primary text-textColor py-3 rounded-full font-semibold"
                                        >
                                            {searchResult?.length ?? initData?.special_offers?.length} Produkte anzeigen
                                        </button>
                                    </div>

                                    {initData &&
                                        <div className="flex flex-col gap-4">
                                            <Filters
                                                initData={initData}
                                                selectedCategory={pendingCategory}
                                                searchValue={pendingSearchValue}
                                                hideSearchButton
                                                autoFocus={isFilterOpen}
                                                focusKey={`${pendingSearchValue}-${pendingCategory}`}
                                                onCategoryChange={(v, source) => {
                                                    setPendingCategory(v);
                                                    pendingCategorySourceRef.current = source;
                                                }}
                                                onSearchChange={(v) => {
                                                    setPendingSearchValue(v);
                                                }}
                                                onSearchSubmit={applyMobileFilters}
                                                onResetFilters={resetMobileFilters}
                                            />
                                        </div>
                                    }
                                </div>
                            </div>
                        )}
                    </main>
                </section>
            </div>
        </>
    );
};

export default CustomersProductList;
